import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getNotifications } from '../store/notifications/actions';
import { toast } from 'sonner';

const useNotificationPolling = (intervalMs = 30000) => { // Default 30 seconds
    const dispatch = useDispatch();
    const intervalRef = useRef(null);
    const prevNewCountRef = useRef(0);
    const isPollingActiveRef = useRef(true);

    const { newNotificationCount, isLoading } = useSelector(state => ({
        newNotificationCount: state.notifications?.newNotificationCount || 0,
        isLoading: state.notifications?.isLoading || false
    }));

    // Optimized polling function
    const pollNotifications = useCallback(() => {
        // Don't poll if already loading or page is not visible
        if (isLoading || document.hidden) {
            return;
        }

        // Silent poll - only fetch first 10 notifications to check for new ones
        dispatch(getNotifications({
            headers: {
                "start-index": 0,
                "record-count": 10,
            },
        }));
    }, [dispatch, isLoading]);

    // Show toast for new notifications
    useEffect(() => {
        if (newNotificationCount > prevNewCountRef.current) {
            const newCount = newNotificationCount - prevNewCountRef.current;
            toast.success(`You have ${newCount} new notification${newCount >
                1 ? 's' : ''}`, {
                duration: 4000,
                position: 'top-right',
            });
        }
        prevNewCountRef.current = newNotificationCount;
    }, [newNotificationCount]);

    // Start polling
    const startPolling = useCallback(() => {
        if (intervalRef.current) return; // Already polling

        isPollingActiveRef.current = true;
        intervalRef.current = setInterval(() => {
            if (isPollingActiveRef.current) {
                pollNotifications();
            }
        }, intervalMs);
    }, [pollNotifications, intervalMs]);

    // Stop polling
    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        isPollingActiveRef.current = false;
    }, []);



    // Auto-start polling on mount
    useEffect(() => {
        startPolling();
        return () => {
            stopPolling();
        };
    }, [startPolling, stopPolling]);

    return {
        startPolling,
        stopPolling,
        isPolling: !!intervalRef.current && isPollingActiveRef.current
    };
};

export default useNotificationPolling;